import React, {
 ChangeEvent,
 useEffect,
 useState,
} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {
 Button,
 DatePickerField,
 KTSVG,
 Model,
} from 'helpers'
import {TextField} from 'helpers/ui-components/form/TextField'
import {Select2DropdownField} from 'helpers/ui-components/form/Select2DropdownField'
import IDropdown from 'models/dropdown'
import {IFileObject} from 'models/Common'
import {IProductCategoryLicense} from 'models/MyAccount'
import {fetchState} from 'utils/api/auth/locationService'
import {generateRandomString} from 'utils/helper'
import {handleFileChange as uploadFileChange} from 'utils/helpers/fileUpload'

export type IMandiLicenseFormPayload = {
 id?: number
 key: string
 state: number
 state_name?: string
 license_number: string
 expiry_date: Date | string
 license_document: string
 license_document_url?: string
}

type IProps = {
 show: boolean
 editLicense?: IProductCategoryLicense | null
 onClose: () => void
 onSave: (values: IMandiLicenseFormPayload) => void
}

const sharedSelectStyle = {
 transition: 'unset',
 boxShadow: 'unset',
 border: '0px',
 borderRadius: '0px',
 padding: '0px',
 minHeight: 'auto',
}

const sharedClassName =
 'font-normal !text-[#33393D] boxed-select2 relative block text-lg border-0 !rounded-l-lg'

const AddMandiLicenseModal = (props: IProps) => {
 const {t} = useTranslation()
 const [stateList, setStateList] = useState<IDropdown[]>([])
 const [uploadedFileData, setUploadedFileData] =
  useState<IFileObject>({})
 const [uploading, setUploading] = useState(false)
 const fileInputId = `license-doc-${generateRandomString(
  5
 )}`

 const {
  register,
  control,
  handleSubmit,
  reset,
  formState: {errors, isValid},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
  defaultValues: {
   state: '' as string | number,
   expiryDate: null as Date | null,
   licenseNumber: '',
  },
 })

 const isEditMode = !!props.editLicense?.id

 const ensureDropdownOption = (
  list: IDropdown[],
  id: number,
  name: string
 ): IDropdown[] => {
  if (list.some((item) => item.id === id)) {
   return list
  }
  return [{id, name, is_active: true}, ...list]
 }

 const toDropdownList = (list: unknown): IDropdown[] =>
  Array.isArray(list) ? (list as IDropdown[]) : []

 const loadStates = async (
  editLicense?: IProductCategoryLicense | null
 ): Promise<IDropdown[]> => {
  const states = toDropdownList(await fetchState())
  if (editLicense?.state) {
   return ensureDropdownOption(
    states,
    Number(editLicense.state),
    editLicense.state_name ?? String(editLicense.state)
   )
  }
  return states
 }

 useEffect(() => {
  if (!props.show) return

  let cancelled = false

  const initForm = async () => {
   setUploadedFileData({})
   const states = await loadStates(props.editLicense)

   if (cancelled) return

   setStateList(states)

   if (props.editLicense) {
    const expiry = props.editLicense.expiry_date
     ? new Date(props.editLicense.expiry_date)
     : null
    reset({
     state: props.editLicense.state,
     expiryDate: expiry,
     licenseNumber: props.editLicense.license_number ?? '',
    })
    return
   }

   reset({
    state: '',
    expiryDate: null,
    licenseNumber: '',
   })
  }

  void initForm()

  return () => {
   cancelled = true
  }
 }, [props.show, props.editLicense, reset])

 const handleClose = () => {
  props.onClose()
 }

 const onLicenseDocumentChange = (
  e: ChangeEvent<HTMLInputElement>
 ) => {
  void uploadFileChange(
   e,
   uploadedFileData,
   setUploading,
   setUploadedFileData,
   'license_document'
  )
 }

 const uploadedLicenseFileName =
  uploadedFileData?.license_document?.fileName
 const existingLicenseDocumentUrl =
  props.editLicense?.license_document_url ||
  props.editLicense?.license_document

 const submitForm = (data: {
  state: string | number
  expiryDate: Date | null
  licenseNumber: string
 }) => {
  const licenseDocument =
   uploadedFileData?.license_document?.filePath ??
   props.editLicense?.license_document ??
   ''

  const selectedState = stateList.find(
   (item) => Number(item.id) === Number(data.state)
  )

  props.onSave({
   ...(props.editLicense?.id && {id: props.editLicense.id}),
   key: props.editLicense?.key || generateRandomString(5),
   state: Number(data.state),
   state_name: selectedState?.name,
   license_number: data.licenseNumber,
   expiry_date: data.expiryDate as Date,
   license_document: licenseDocument,
   ...(uploadedFileData?.license_document?.filePath && {
    license_document_url:
     uploadedFileData.license_document.filePath,
   }),
  })
 }

 return (
  <Model
   show={props.show}
   showHeader={false}
   closeBtnClass='absolute right-4 top-2 !bg-[#E6EBF0] !rounded-3xl w-7 h-7 !text-[#33393D]'
   header=''
   close={handleClose}
   modelClass='md:w-[800px]'
   modelContainerClass='!rounded-2xl'
   modelBodyClass='p-10'
  >
   <div className='w-full'>
    <p className='text-[#171B1F] text-left font-bold text-2xl'>
     {isEditMode
      ? t(
         'myAccountPage.businessProfile.offtaker.mandiLicense.editLicense'
        )
      : t(
         'myAccountPage.businessProfile.offtaker.mandiLicense.addLicense'
        )}
    </p>
    <form
     method='POST'
     action=''
     onSubmit={handleSubmit(submitForm)}
    >
     <div className='grid md:grid-cols-2 gap-5 w-full mt-7 text-[#171B1F]'>
      <Select2DropdownField
       label={t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.geographyLevel'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className={sharedClassName}
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       required={true}
       disabled={false}
       isClearable={false}
       isSearchable={true}
       control={control}
       customStyle={sharedSelectStyle}
       data={stateList}
       rules={{
        validate: {
         required: (v: string) => {
          if (v === '' || v === undefined || v === null)
           return `${t('helperText.plsSelect')} ${t(
            'myAccountPage.businessProfile.offtaker.mandiLicense.geographyLevel'
           )}`
         },
        },
       }}
       id='state'
       name='state'
       OptionValueKey='id'
       OptionLabelKey='name'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.selectState'
       )}
       errors={errors}
      />
      <DatePickerField
       label={t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.expiryDate'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className='!pl-0 !py-0 !text-[#33393D]'
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       required={true}
       id='expiryDate'
       name='expiryDate'
       dateFormat='dd-MM-yyyy'
       popperClassName='!z-[60]'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.expiryDatePlaceholder'
       )}
       control={control}
       rules={{
        validate: {
         required: (v: Date | null) => {
          if (!v)
           return `${t('helperText.plsSelect')} ${t(
            'myAccountPage.businessProfile.offtaker.mandiLicense.expiryDate'
           )}`
         },
        },
       }}
       errors={errors}
      />
      <TextField
       label={t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.licenseNumber'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className='!text-[#33393D]'
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       type='text'
       required={true}
       id='licenseNumber'
       name='licenseNumber'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.enterLicenseNumber'
       )}
       register={register('licenseNumber', {
        required: `${t('helperText.plsEnter')} ${t(
         'myAccountPage.businessProfile.offtaker.mandiLicense.licenseNumber'
        )}`,
       })}
       errors={errors}
      />
     </div>

     <div className='w-full mt-6'>
      <p className='text-[#171B1F] text-base font-bold mb-3'>
       {t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.uploadLicense'
       )}
      </p>
      <label
       htmlFor={fileInputId}
       className='flex items-center gap-3 w-full min-h-[88px] cursor-pointer rounded-lg border border-dashed border-[#D7DFEA] bg-[#F5F8FA] px-5 py-4'
      >
       <KTSVG
        path='/media/icons/uploadFile.svg'
        svgClassName='w-5 h-5 shrink-0'
       />
       <div className='flex flex-col'>
        <span className='text-[#171B1F] text-sm font-bold'>
         {uploading ? (
          t('bulkUpload.uploading')
         ) : uploadedLicenseFileName ? (
          uploadedLicenseFileName
         ) : existingLicenseDocumentUrl ? (
          <a
           href={existingLicenseDocumentUrl}
           target='_blank'
           rel='noreferrer'
           className='text-[#4E83E4]'
           onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            window.open(
             existingLicenseDocumentUrl,
             '_blank',
             'noopener,noreferrer'
            )
           }}
          >
           {t(
            'myAccountPage.businessProfile.offtaker.mandiLicense.uploadedDocument'
           )}
          </a>
         ) : (
          t(
           'myAccountPage.businessProfile.offtaker.paymentTerms.clickToUpload'
          )
         )}
        </span>
        <span className='text-[#80868C] text-xs mt-0.5'>
         {t(
          'myAccountPage.businessProfile.offtaker.paymentTerms.supportedFormats'
         )}
        </span>
       </div>
      </label>
      <input
       id={fileInputId}
       name='license_document'
       type='file'
       className='hidden'
       accept='.pdf,.jpg,.jpeg,.png'
       disabled={uploading}
       onChange={onLicenseDocumentChange}
      />
     </div>

     <div className='flex w-full gap-4 mt-8'>
      <Button
       type='button'
       onClick={handleClose}
       color='blue'
       className='flex-1 h-12 text-center bg-white !rounded-lg text-base !text-[#4E83E4] font-semibold border border-[#4E83E4]'
      >
       {t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.cancel'
       )}
      </Button>
      <Button
       type='submit'
       color='blue'
       disabled={!isValid || uploading}
       className='flex-1 h-12 text-center bg-[#4E83E4] !rounded-lg text-base text-white font-semibold border border-[#4E83E4]'
      >
       {t(
        'myAccountPage.businessProfile.offtaker.mandiLicense.save'
       )}
      </Button>
     </div>
    </form>
   </div>
  </Model>
 )
}

export default AddMandiLicenseModal
